import type * as React from 'react';

import { Icon } from '@gitbook/spine-icons';

import { toast } from './Toaster';

type WriteClipboardOptions = {
    /**
     * Whether to notify the user about the action in a toast.
     * Can be a boolean or an object with the title of the toast.
     * @default false
     */
    notify?: boolean | { title: React.ReactNode };
};

/**
 * Write a text into the system clipboard.
 * Notifies errors with a toast if it fails.
 */
async function writeText(data: string, options?: WriteClipboardOptions): Promise<void> {
    await wrapCopy(async () => {
        await navigator.clipboard.writeText(data);
    }, options);
}

/**
 * Write a text into the system clipboard.
 * Notifies errors with a toast if it fails.
 */
async function writeData(
    data: {
        [contentType: string]: string;
    },
    options?: WriteClipboardOptions
): Promise<void> {
    await wrapCopy(async () => {
        await navigator.clipboard.write([new ClipboardItem(data)]);
    }, options);
}

async function wrapCopy(fn: () => Promise<void>, options?: WriteClipboardOptions) {
    const notify = options?.notify ?? true;
    await toast.catch(async () => {
        await fn();
        if (notify) {
            const title = notify === true ? 'Copied to clipboard' : notify.title;
            toast(title, { icon: Icon.Copy });
        }
    }, 'Failed to copy to clipboard');
}

export const clipboard = { writeText, writeData };
